import type { ManualBackupSnapshot, ManualHealthDataStore } from '@/domain/health/contracts'

import {
  BACKUP_ERROR_MESSAGE,
  DEFAULT_CRYPTO_POLICY,
  decryptBackup,
  encryptBackup,
  type CryptoPolicy,
} from './crypto'

export interface BackupCounts {
  metrics: number
  sleeps: number
  workouts: number
  intakes: number
  savedFoods: number
  moods: number
  periods: number
}

export interface BackupInspection {
  snapshot: ManualBackupSnapshot
  counts: BackupCounts
}

export interface ExportedBackup {
  blob: Blob
  filename: string
}

function manualOnly(snapshot: ManualBackupSnapshot): ManualBackupSnapshot {
  return {
    metrics: snapshot.metrics.filter((record) => record.source === 'manual'),
    sleeps: snapshot.sleeps.filter((record) => record.source === 'manual'),
    workouts: snapshot.workouts.filter((record) => record.source === 'manual'),
    settings: structuredClone(snapshot.settings),
    workoutOverrides: structuredClone(snapshot.workoutOverrides ?? []),
    intakes: (snapshot.intakes ?? []).filter((record) => record.source === 'manual'),
    savedFoods: structuredClone(snapshot.savedFoods ?? []),
    moods: (snapshot.moods ?? []).filter((record) => record.source === 'manual'),
    periods: (snapshot.periods ?? []).filter((record) => record.source === 'manual'),
  }
}

function countsOf(snapshot: ManualBackupSnapshot): BackupCounts {
  return {
    metrics: snapshot.metrics.length,
    sleeps: snapshot.sleeps.length,
    workouts: snapshot.workouts.length,
    intakes: snapshot.intakes?.length ?? 0,
    savedFoods: snapshot.savedFoods?.length ?? 0,
    moods: snapshot.moods?.length ?? 0,
    periods: snapshot.periods?.length ?? 0,
  }
}

export class BackupService {
  constructor(
    private readonly store: ManualHealthDataStore,
    private readonly policy: CryptoPolicy = DEFAULT_CRYPTO_POLICY,
    private readonly now: () => Date = () => new Date(),
  ) {}

  async exportEncrypted(password: string): Promise<ExportedBackup> {
    const snapshot = manualOnly(await this.store.readManualSnapshot())
    const exportedAt = this.now()
    const envelope = await encryptBackup(
      { schemaVersion: 1, exportedAt: exportedAt.toISOString(), ...snapshot },
      password,
      this.policy,
    )
    return {
      blob: new Blob([JSON.stringify(envelope)], { type: 'application/json' }),
      filename: `carebaby-backup-${exportedAt.toISOString().slice(0, 10)}.json`,
    }
  }

  async inspectEncrypted(file: string, password: string): Promise<BackupInspection> {
    try {
      const envelope: unknown = JSON.parse(file)
      const payload = await decryptBackup(envelope, password)
      const snapshot: ManualBackupSnapshot = {
        metrics: structuredClone(payload.metrics),
        sleeps: structuredClone(payload.sleeps),
        workouts: structuredClone(payload.workouts),
        settings: structuredClone(payload.settings),
        workoutOverrides: structuredClone(payload.workoutOverrides ?? []),
        intakes: structuredClone(payload.intakes ?? []),
        savedFoods: structuredClone(payload.savedFoods ?? []),
        moods: structuredClone(payload.moods ?? []),
        periods: structuredClone(payload.periods ?? []),
      }
      return { snapshot, counts: countsOf(snapshot) }
    } catch {
      throw new Error(BACKUP_ERROR_MESSAGE)
    }
  }

  async restoreInspected(inspection: BackupInspection): Promise<BackupCounts> {
    await this.store.replaceManualSnapshot(structuredClone(inspection.snapshot))
    return countsOf(inspection.snapshot)
  }

  async clearAll(): Promise<void> {
    await this.store.clearManualData()
  }
}
